import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, Signal, SimpleChanges, ViewChild, ViewEncapsulation } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatOptionModule } from '@angular/material/core'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatSelect, MatSelectModule } from '@angular/material/select'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatTooltipModule } from '@angular/material/tooltip'
import { PipesModule } from '@mmx/pipes'
import { AppointmentSelectOption } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

import { EstimateBalanceFlowService } from '../../services/estimate-balance-flow.service'
import { EstimateBalanceStateService } from '../../services/estimate-balance-state.service'

@Component({
  selector: 'mmx-balance-charges-appointment-picker',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // None — the panel-class rule must reach mat-select's overlay (rendered outside the view).
  // Rules are scoped under a single root class to prevent global leakage; see the .scss.
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTooltipModule,
    PipesModule,
  ],
  templateUrl: './balance-charges-appointment-picker.component.html',
  styleUrls: ['./balance-charges-appointment-picker.component.scss'],
})
export class BalanceChargesAppointmentPickerComponent implements OnChanges, OnDestroy {
  /** `__balance.` prefix collides with no real Dynamo id, safe as a sentinel value. */
  static readonly UNSCHEDULED_OPTION_VALUE = '__balance.unscheduled__'

  /**
   * Sentinel for the CoC-toggle mat-option (lives inside the dropdown so users can flip
   * CoC without closing the panel). Must be non-null — mat-select's "deselect on nullable
   * click" branch would otherwise wipe the appointment selection in single mode.
   */
  static readonly COC_TOGGLE_OPTION_VALUE = '__balance.coc-toggle__'

  @Input() patientId: string | null = null
  @Input() selectedIds: string[] = []
  @Input() disabled = false
  @Input() cocMode = false
  @Input() unscheduledSelected = false
  @Input() readOnly = false
  /** True once the estimate is persisted with a mode set — backend rejects mode flips on update, so lock the toggle both ways. */
  @Input() modeLocked = false

  @Output() readonly selectionChange = new EventEmitter<string[]>()
  @Output() readonly cocModeChange = new EventEmitter<boolean>()
  @Output() readonly unscheduledChange = new EventEmitter<boolean>()

  @ViewChild(MatSelect) private readonly matSelect?: MatSelect

  readonly UNSCHEDULED_OPTION_VALUE = BalanceChargesAppointmentPickerComponent.UNSCHEDULED_OPTION_VALUE
  readonly COC_TOGGLE_OPTION_VALUE = BalanceChargesAppointmentPickerComponent.COC_TOGGLE_OPTION_VALUE

  /** Picker owns the fetch + transform; parent only passes `patientId`. */
  readonly options: Signal<AppointmentSelectOption[]> = this.state.appointmentOptions

  // FormControl (not [value]) so mat-select re-applies the selection when options arrive
  // after the value. Always `multiple`; single-mode UX is emulated in onChange (truncate +
  // close) and via SCSS that hides checkboxes.
  readonly control = new FormControl<string[]>([], { nonNullable: true })

  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly state: EstimateBalanceStateService,
    private readonly flow: EstimateBalanceFlowService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['patientId']) {
      if (this.patientId) {
        this.flow.loadAppointmentsForPatient(this.patientId).pipe(takeUntil(this.destroy$)).subscribe()
      } else {
        this.state.setAppointments([])
      }
    }
    // Recompute the control value from both inputs so the parent never has to know about
    // the picker-private UNSCHEDULED_OPTION_VALUE sentinel.
    if (changes['selectedIds'] || changes['unscheduledSelected']) {
      const next = this.unscheduledSelected
        ? [this.UNSCHEDULED_OPTION_VALUE]
        : [...this.selectedIds]
      if (!this.idsEqual(this.control.value, next)) {
        this.control.setValue(next, { emitEvent: false })
      }
    }
    if (changes['disabled'] || changes['readOnly']) {
      const shouldDisable = this.disabled || this.readOnly
      shouldDisable ? this.control.disable({ emitEvent: false }) : this.control.enable({ emitEvent: false })
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  trackByValue(_index: number, option: AppointmentSelectOption): string {
    return option.value
  }

  onChange(value: string[]): void {
    if (value.includes(this.COC_TOGGLE_OPTION_VALUE)) {
      // Reached only via the toggle row's label-area click (the slide-toggle stops propagation).
      // The cocMode flip runs through onCocToggle; here we just strip the sentinel so
      // mat-select's selection model stays clean and the panel stays open.
      const clean = value.filter(v => v !== this.COC_TOGGLE_OPTION_VALUE)
      if (!this.idsEqual(this.control.value, clean)) {
        this.control.setValue(clean, { emitEvent: false })
      }
      return
    }

    const unscheduled = value.includes(this.UNSCHEDULED_OPTION_VALUE)
    let scheduledIds = value.filter(v => v !== this.UNSCHEDULED_OPTION_VALUE)

    if (!this.cocMode && scheduledIds.length > 1) {
      // Single-mode on a multi mat-select: keep only the newly added id (or the last as a
      // fallback if stale state somehow leaked).
      const previous = new Set(this.selectedIds)
      const newlyAdded = scheduledIds.find(id => !previous.has(id))
      scheduledIds = newlyAdded ? [newlyAdded] : [scheduledIds[scheduledIds.length - 1]]
    }
    if (!this.cocMode) {
      // mat-select doesn't auto-close in multiple mode — mirror single-mode UX.
      this.matSelect?.close()
    }
    // Emit unscheduled first so the container updates state.unscheduled before the empty
    // scheduled selection lands — otherwise the flow service's persisted-sync guard races the clear.
    this.unscheduledChange.emit(unscheduled)
    this.selectionChange.emit(scheduledIds)
  }

  isUnscheduledDisabled(): boolean {
    if (this.modeLocked) {
      return true
    }
    const selected = this.control.value ?? []
    const hasScheduled = selected.some(id => id !== this.UNSCHEDULED_OPTION_VALUE)
    return hasScheduled
  }

  // Two block reasons: persisted-estimate mode lock, and the scheduled↔unscheduled exclusivity
  // rule. Empty string = no tooltip.
  unscheduledTooltipKey(): string {
    if (this.modeLocked) {
      return 'balance.charges.unscheduled-mode-locked-tooltip'
    }
    if (this.isUnscheduledDisabled()) {
      return 'balance.charges.unscheduled-exclusivity-tooltip'
    }
    return ''
  }

  isScheduledOptionDisabled(optionValue: string): boolean {
    const selected = this.control.value ?? []
    const hasUnscheduled = selected.includes(this.UNSCHEDULED_OPTION_VALUE)
    // Already-checked rows stay enabled so the user can deselect them — only the
    // (unscheduled-selected ∧ not-yet-checked) combination blocks.
    return hasUnscheduled && !selected.includes(optionValue)
  }

  // On a saved unscheduled estimate, CoC-off would cascade into a server-immutable mode flip
  // — lock it. Scheduled estimates keep CoC toggleable (off doesn't change mode).
  isCocToggleDisabled(): boolean {
    return this.modeLocked && this.unscheduledSelected
  }

  onCocToggle(): void {
    if (this.isCocToggleDisabled()) {
      return
    }
    this.cocModeChange.emit(!this.cocMode)
  }

  collapseToFirst(options: AppointmentSelectOption[]): string[] {
    const selected = options.filter(o => o.selected)
    if (selected.length === 0) {
      return []
    }
    const first = selected
      .slice()
      .sort((a, b) => Number(a.appointment?.startAt?.valueOf() ?? 0) - Number(b.appointment?.startAt?.valueOf() ?? 0))[0]
    return [first.value]
  }

  private idsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false
    }
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false
      }
    }
    return true
  }
}
